import type { ReactNode } from 'react';
import { createRef, Component } from 'react';

import CloseIcon from '@skyscanner/backpack-web/bpk-component-icon/sm/close';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner/backpack-web/bpk-component-text';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import { withI18n } from '../../contexts/i18n';
import NoneStyleButton from '../NoneStyleButton';

import type { I18nService } from '@shared/types/i18n';
import type { Maybe } from '@shared/types/utils';

import STYLES from './ModalLayout.module.scss';

import {
  disableBodyScroll,
  enableBodyScroll,
} from '@shared/utils-frontend/body-scroll-lock';

type Props = {
  i18n: I18nService;
  onClose?: any;
  id: string;
  children: ReactNode;
  title?: ReactNode;
  withoutPadding?: boolean;
  footerClassName?: Maybe<string>;
  headerClassName?: Maybe<string>;
  href?: Maybe<string>;
  footer?: ReactNode | ((onClick: Function) => ReactNode);
  header?: ReactNode;
  divideFooter?: boolean;
  dragBarClassName?: string;
  clearBtn?: ReactNode;
  enableScrollDivider?: boolean;
  noBottomPaddingBeyondFooter?: boolean;
};

type State = {
  scrollDivider: boolean;
};

const defaultProps = {
  title: null,
  onClose: null,
  withoutPadding: false,
  footerClassName: null,
  headerClassName: null,
  href: null,
  footer: null,
  header: null,
  divideFooter: false,
  enableScrollDivider: false,
  noBottomPaddingBeyondFooter: false,
};

const cls = cssModules(STYLES);
const threshold = 5;

class ModalLayout extends Component<Props, State> {
  modalBody: any;

  startY: number;

  lastY: number;

  vx: number;

  draging: boolean;

  transition: string;

  sectionEl: any;

  containerEl: any;

  static defaultProps = defaultProps;

  state: State = {
    scrollDivider: false,
  };

  constructor(props: Props) {
    super(props);

    this.modalBody = createRef();
    this.startY = 0;
    this.lastY = 0;
    this.vx = 0;
    this.draging = false;
    this.transition = '';
    this.sectionEl = null;
    this.containerEl = null;
  }

  componentDidMount() {
    if (this.props.enableScrollDivider) {
      this.showScrollDivider();
    }
    if (this.modalBody.current) {
      disableBodyScroll(this.modalBody.current);
    }
    const { id } = this.props;
    if (!id) {
      return;
    }
    const section = document.querySelector(`#${id}`);
    if (!section) {
      return;
    }
    this.sectionEl = section;
    this.containerEl = section.parentNode;
    this.containerEl.style.overflow = 'hidden';
    this.touch2click();

    if (this.props.href) {
      window.addEventListener('popstate', this.props.onClose);
    }
  }

  componentWillUnmount() {
    if (this.modalBody.current) {
      enableBodyScroll(this.modalBody.current);
    }

    if (this.props.href) {
      window.removeEventListener('popstate', this.props.onClose);
    }
  }

  showScrollDivider = () => {
    const ele = this.modalBody.current;
    if (!ele) {
      return;
    }
    const hasScrollableContent = ele.scrollHeight > ele.clientHeight;
    if (hasScrollableContent) {
      this.setState({
        scrollDivider: true,
      });
    }
  };

  onCloseModal = () => {
    const { href, onClose } = this.props;
    onClose();
    if (href && window.location.href.includes(href)) {
      // Solve history stack add double same page when click close icon
      window.history.back();
    }
  };

  // TODO: The BpkScrim used by the BpkModal is listen 'touchStart' event to close modal.
  // But on the bussiness component, it listens 'click' event which is delay 300ms after 'touchStart'
  // That cause 'click penetrate'.
  // But it is another problem if use 'touchStart' on the bussiness component now.
  // In this component, remove the `touchStart` event and bind 'click' event to close modal
  touch2click = () => {
    const { onClose } = this.props;
    if (!onClose) {
      return;
    }
    const scrim = this.containerEl.querySelector(
      'div[class^=bpk-scrim_bpk-scrim]',
    );
    if (!scrim) {
      return;
    }
    scrim.removeEventListener('mousedown', onClose);
    scrim.removeEventListener('touchstart', onClose);
    scrim.addEventListener('click', this.onCloseModal);
  };

  touchStart = (e: any) => {
    if (!this.sectionEl) {
      return;
    }
    this.vx = 0;
    this.transition = getComputedStyle(this.sectionEl).transition;
    this.sectionEl.style.transition = 'none';
    this.sectionEl.style.webkitTransition = 'none';
    this.startY = e.targetTouches[0].clientY;
  };

  touchMove = (e: any) => {
    if (!this.sectionEl) {
      return;
    }
    const currentY = e.targetTouches[0].clientY;
    this.vx = this.vx * 0.5 + (currentY - this.lastY) * 0.5;
    this.lastY = currentY;
    const deltaY = this.lastY - this.startY;
    if (deltaY < 0) {
      return;
    }
    this.draging = true;
    this.sectionEl.style.transform = `translateY(${deltaY}px)`;
  };

  touchEnd = () => {
    if (!this.sectionEl || !this.draging) {
      return;
    }
    this.sectionEl.style.transition = this.transition;
    this.sectionEl.style.webkitTransition = this.transition;
    const moveDistance = this.lastY - this.startY;
    const sectionHeight = this.sectionEl.getBoundingClientRect().height;
    if (moveDistance > sectionHeight / 2 || this.vx > threshold) {
      this.onCloseModal();
    } else {
      this.sectionEl.style.transform = `translateY(0px)`;
    }
  };

  render() {
    const {
      children,
      clearBtn,
      divideFooter,
      dragBarClassName,
      footer,
      footerClassName,
      header,
      headerClassName,
      i18n: { translate },
      noBottomPaddingBeyondFooter,
      onClose,
      title,
      withoutPadding,
    } = this.props;
    const { scrollDivider } = this.state;
    return (
      <>
        <div
          onTouchStart={this.touchStart}
          onTouchMove={this.touchMove}
          onTouchEnd={this.touchEnd}
        >
          <div className={cls('ModalLayout__dragBar', dragBarClassName)} />

          <header className={cls('ModalLayout__header', headerClassName)}>
            {header || (
              <>
                <div className={cls('ModalLayout__btn')}>
                  <NoneStyleButton
                    className={cls('ModalLayout__expandClickArea')}
                    onClick={this.onCloseModal}
                    aria-label={translate('Modal_close')}
                  >
                    <CloseIcon data-testid="close-modal-button" />
                  </NoneStyleButton>
                </div>
                {title && (
                  <BpkText
                    textStyle={TEXT_STYLES.label1}
                    tagName="h3"
                    className={cls('ModalLayout__headerTitle')}
                  >
                    {title}
                  </BpkText>
                )}
                <div
                  className={cls(
                    'ModalLayout__btn',
                    'ModalLayout__clearAllBtn',
                  )}
                >
                  {clearBtn}
                </div>
              </>
            )}
          </header>
        </div>
        <div
          ref={this.modalBody}
          className={cls(
            'ModalLayout__body',
            footer && !scrollDivider && !noBottomPaddingBeyondFooter
              ? 'ModalLayout__bodyBottomPadding'
              : null,
            withoutPadding ? 'ModalLayout__body--withoutPadding' : null,
          )}
        >
          {children}
        </div>
        {footer && (
          <footer
            className={cls(
              'ModalLayout__footer',
              scrollDivider && 'ModalLayout__footer--scrollDivider',
              divideFooter || scrollDivider
                ? 'ModalLayout__divideFooter'
                : null,
              footerClassName,
            )}
          >
            {typeof footer === 'function' ? footer(onClose) : footer}
          </footer>
        )}
      </>
    );
  }
}

export default withI18n(ModalLayout);
